import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../../../providers/userContextProvider';
import { useQueryBuilder } from './utils';
import {
  Typography, Box,
  Skeleton,
} from '../../../../1-primative';
import { BarChart, Card, CardContent } from '../../../../2-component';
import { TranslatedString } from '../../../../../interfaces/shared';
import { formatMoneyValue } from '../../../../../util';
import { translateBackend } from '../../../../../assets/i18n/config';

const totalManagementFeesQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'type',
      comparison: 'EQUALS',
      value: 'MANAGEMENT_FEE',
    },
    {
      field: 'date',
      comparison: 'GREATER_THAN',
      value: dayjs().startOf('year').format('YYYY-MM-DD'),
    },
  ],
  type: 'TRANSACTION',
  grouping: {
    field: 'date',
    interval: 'MONTH',
    type: 'DATE',
    valueAggregation: 'SUM',
    valueField: 'valueCents',
  },
});

export const FeesAndRevenue = ({ options }: { options: {
  customTitle: TranslatedString,
} }) => {
  const {
    customTitle,
  } = options;
  const { activeOrganization } = useContext(UserContext);
  const [totalValue, setTotalValue] = useState(0);
  const { t } = useTranslation(['homePageConfiguration', 'accountTypes']);

  const { data, loading, refetch } = useQueryBuilder(totalManagementFeesQuery(activeOrganization));

  useEffect(() => {
    refetch();
  }, [activeOrganization, refetch]);

  useEffect(() => {
    setTotalValue(data?.generateReport?.grouping.reduce((acc: number, curr: any) => acc + curr.value, 0) ?? 0);
  }, [data]);

  return (
    <Card sx={{ mt: 2, height: '350px' }}>
      <CardContent sx={{
        paddingBottom: '16px! important', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 'calc(100% - 32px)',
      }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant='titleLarge'>{translateBackend(customTitle)}</Typography>
          {loading ? (
            <Skeleton height='24px' width='150px' />
          ) : (
            <Typography variant='bodyLarge' colorVariant='variant' sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoneyValue(totalValue * -1)}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }}>
          {loading ? (
            <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='end' height='100%' gap={4}>
              <Skeleton variant='rectangular' height='20%' width='100%' />
              <Skeleton variant='rectangular' height='60%' width='100%' />
              <Skeleton variant='rectangular' height='100%' width='100%' />
              <Skeleton variant='rectangular' height='40%' width='100%' />
              <Skeleton variant='rectangular' height='80%' width='100%' />
              <Skeleton variant='rectangular' height='90%' width='100%' />
              <Skeleton variant='rectangular' height='40%' width='100%' />
            </Box>
          ) : totalValue === 0 ? (
            <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
              <Typography variant='bodyLarge' weight='bold'>
                {t('feesAndRevenue.noFeesCollectedYet')}
              </Typography>
            </Box>
          ) : (
            <BarChart
              labels={data?.generateReport?.grouping.map((x: any) => dayjs(new Date(x.key)).utc().format('MMM').toUpperCase())}
              datasets={[{ label: '', data: data?.generateReport?.grouping.map((x: any) => x.value * -1) }]}
              noYAxis
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
